import {
  attachmentKind,
  collectAttachmentMedia,
  worksheetPhotoFromUrl,
  type AttachmentFile,
  type WorksheetPhoto,
} from '@/components/common/worksheetPhoto';
import { CHAT_CHANNEL_TYPE, type ChatMessage } from '@/types/chat';

import {
  citedPages,
  libraryDocumentHref,
  libraryDocumentLabel,
  readLibraryDocuments,
} from './libraryDocuments';
import {
  readExternalResourceLink,
  type ExternalResourceLink,
} from './toolOutput';

/**
 * Files and links assistant turns in this live thread already returned.
 * Tool results and the reply text both count: later answers link job
 * sheets and photos in the sentence after the tool payload is shortened.
 * A later reply that has none leaves the earlier ones in place.
 * Reloaded history keeps tool names only, so a later phase has to
 * persist these before the drawer can survive a refresh.
 */
export type TurnAsset =
  | {
      id: string;
      kind: 'image';
      label: string;
      photo: WorksheetPhoto;
    }
  | {
      id: string;
      kind: 'pdf';
      label: string;
      file: AttachmentFile;
    }
  | {
      id: string;
      kind: 'file';
      label: string;
      file: AttachmentFile;
    }
  | {
      id: string;
      kind: 'link';
      label: string;
      href: string;
      service: ExternalResourceLink['service'];
    }
  | {
      id: string;
      kind: 'library';
      label: string;
      documentId: string;
      /** Knowledge Base page for the document at the first of `pages`, with a new chat open. */
      href: string;
      /** Pages the reply cites, else the pages Connie opened. */
      pages: number[];
    };

/** One pair of parentheses, so a name like document(51630).pdf stays whole. */
const ATTACHMENT_URL =
  'https:\\/\\/s3-eu-west-1\\.amazonaws\\.com\\/bigchange\\/Attachment\\/(?:[^()\\s"\'<>]|\\([^()\\s"\'<>]*\\))+';
const MARKDOWN_ATTACHMENT = new RegExp(
  `!?\\[([^\\]\\n]*)\\]\\((${ATTACHMENT_URL})\\)`,
  'g'
);
const BARE_ATTACHMENT = new RegExp(ATTACHMENT_URL, 'g');
const GENERIC_LABEL = new Set(['Photo', 'PDF', 'File']);

const fileLabel = (file: AttachmentFile) => {
  if (file.label) return file.label;
  return file.kind === 'pdf' ? 'PDF' : 'File';
};

const assetFromUrl = (url: string, label?: string): TurnAsset | null => {
  const kind = attachmentKind(url);
  const text = label?.replace(/\s+/g, ' ').trim();
  const clean = text && text !== url ? text : undefined;

  if (kind === 'image') {
    const photo = worksheetPhotoFromUrl(url, clean);
    if (!photo) return null;
    return {
      id: `image:${photo.url}`,
      kind: 'image',
      label: photo.label ?? 'Photo',
      photo,
    };
  }

  if (kind === 'pdf' || kind === 'file') {
    return {
      id: `file:${url}`,
      kind,
      label: clean ?? (kind === 'pdf' ? 'PDF' : 'File'),
      file: { url, kind, label: clean },
    };
  }

  return null;
};

const assetsFromContent = (content: string): TurnAsset[] => {
  const found: TurnAsset[] = [];
  const seen = new Set<string>();
  const linked = new Set<string>();

  for (const match of content.matchAll(MARKDOWN_ATTACHMENT)) {
    const url = match[2];
    linked.add(url);
    const asset = assetFromUrl(url, match[1]);
    if (!asset || seen.has(asset.id)) continue;
    seen.add(asset.id);
    found.push(asset);
  }

  for (const match of content.matchAll(BARE_ATTACHMENT)) {
    const url = match[0].replace(/[.,;]+$/g, '');
    if (linked.has(url)) continue;
    const asset = assetFromUrl(url);
    if (!asset || seen.has(asset.id)) continue;
    seen.add(asset.id);
    found.push(asset);
  }

  return found;
};

const relabel = (asset: TurnAsset, label: string): TurnAsset => {
  switch (asset.kind) {
    case 'image':
      return { ...asset, label, photo: { ...asset.photo, label } };
    case 'pdf':
    case 'file':
      return { ...asset, label, file: { ...asset.file, label } };
    case 'link':
    case 'library':
      return { ...asset, label };
    default: {
      const _exhaustive: never = asset;
      return _exhaustive;
    }
  }
};

const mergePages = (current: number[], next: number[]) =>
  [...new Set([...current, ...next])].sort((a, b) => a - b);

const libraryAssets = (
  outputs: NonNullable<ChatMessage['toolCallOutputs']>,
  content: string
): TurnAsset[] => {
  const assets: Extract<TurnAsset, { kind: 'library' }>[] = [];

  for (const output of outputs) {
    for (const ref of readLibraryDocuments(output.toolName, output.output)) {
      const current = assets.find(asset => asset.documentId === ref.documentId);
      if (current) {
        current.pages = mergePages(current.pages, ref.pagesRead);
        if (ref.name && current.label !== ref.name) current.label = ref.name;
        continue;
      }
      assets.push({
        id: `library:${ref.documentId}`,
        kind: 'library',
        label: libraryDocumentLabel(ref),
        documentId: ref.documentId,
        href: libraryDocumentHref(ref.documentId),
        pages: ref.pagesRead,
      });
    }
  }

  for (const asset of assets) {
    const cited = citedPages(content, asset.label);
    if (cited.length) asset.pages = mergePages([], cited);
    asset.href = libraryDocumentHref(
      asset.documentId,
      asset.pages.length ? Math.min(...asset.pages) : undefined
    );
  }

  return assets;
};

const assetsFromOutputs = (
  outputs: NonNullable<ChatMessage['toolCallOutputs']>
): TurnAsset[] => {
  const media = collectAttachmentMedia(outputs);
  const assets: TurnAsset[] = [];

  for (const photo of media.photos) {
    assets.push({
      id: `image:${photo.url}`,
      kind: 'image',
      label: photo.label ?? 'Photo',
      photo,
    });
  }

  for (const file of media.files) {
    if (file.kind === 'pdf') {
      assets.push({
        id: `file:${file.url}`,
        kind: 'pdf',
        label: fileLabel(file),
        file,
      });
      continue;
    }

    assets.push({
      id: `file:${file.url}`,
      kind: 'file',
      label: fileLabel(file),
      file,
    });
  }

  const seenLinks = new Set<string>();
  for (const output of outputs) {
    const link = readExternalResourceLink(output.toolName, output.output);
    if (!link || seenLinks.has(link.href)) continue;
    seenLinks.add(link.href);
    assets.push({
      id: `link:${link.href}`,
      kind: 'link',
      label: link.label,
      href: link.href,
      service: link.service,
    });
  }

  return assets;
};

const consider = (assets: TurnAsset[], asset: TurnAsset) => {
  const index = assets.findIndex(current => current.id === asset.id);
  if (index < 0) {
    assets.unshift(asset);
    return;
  }

  const current = assets[index];
  if (current.kind === 'library' && asset.kind === 'library') {
    // A later turn that cites other pages of the same document wins.
    assets[index] = asset.pages.length ? asset : current;
    return;
  }
  if (GENERIC_LABEL.has(current.label) && !GENERIC_LABEL.has(asset.label)) {
    assets[index] = relabel(current, asset.label);
  }
};

export function collectTurnAssets(messages: ChatMessage[]): TurnAsset[] {
  const assets: TurnAsset[] = [];

  for (const message of messages) {
    if (message.type !== CHAT_CHANNEL_TYPE.BOT) continue;

    const outputs = message.toolCallOutputs;
    if (outputs?.length) {
      for (const asset of assetsFromOutputs(outputs)) consider(assets, asset);
      for (const asset of libraryAssets(outputs, message.content ?? '')) {
        consider(assets, asset);
      }
    }

    if (message.content) {
      for (const asset of assetsFromContent(message.content)) {
        consider(assets, asset);
      }
    }
  }

  return assets;
}
